
import React, { useState, useMemo } from 'react';
import type { LabelConfig, IconType, IconCategory, CustomTemplate } from '../types';
import { COLOR_THEMES, ICONS } from '../constants';
import { generateLabelSvg } from '../lib/svgGenerator';
import { useFontsVersion } from '../lib/fontsVersion';
import DynamicSvgIcon from './DynamicSvgIcon';
import AiIconGenerator from './AiIconGenerator';

export type WizardShape = 'square' | 'rectangle' | 'circle' | 'template';

export interface WizardShapeConfig {
  id: WizardShape;
  label: string;
  w: number;
  h: number;
  shapeId?: string;
  variantId: string;
  defaultCount: number;
}

export const WIZARD_SHAPES: WizardShapeConfig[] = [
  { id: 'square',    label: 'Čtverec',  w: 26, h: 26, variantId: 'square-sm',    defaultCount: 14 },
  { id: 'circle',    label: 'Kruh',     w: 26, h: 26, shapeId: 'circle', variantId: 'circle-sm', defaultCount: 21 },
  { id: 'rectangle', label: 'Obdélník', w: 26, h: 16, variantId: 'rectangle-md', defaultCount: 21 },
];

/** What the admin saved as the default for a shape's sticker slot; the wizard starts the shape from it. */
export interface WizardShapeDefault {
  icon?: IconType;
  count?: number;
  /** The slot is a template: the wizard offers it as that template (at this size) instead of the plain shape. */
  template?: { id: string; w: number; h: number };
}

type WrapMode = '1' | '2' | '3';

// The wrap mode picked on step 1 applies to every shape — the customer types one name and expects it
// laid out the same on all of them (step 2 can still change it per shape).
const ALL_WRAP_TARGETS: WizardShape[] = [...WIZARD_SHAPES.map(s => s.id), 'template'];

interface Props {
  nameLabel: string;
  classNameLabel: string;
  defaultNameValue?: string;
  defaultClassNameValue?: string;
  /** Admin-editable texts (settings/general); empty falls back to the built-in wording. */
  kicker?: string;
  title?: string;
  intro?: string;
  icons: IconType[];
  categories: IconCategory[];
  allowAi: boolean;
  customTemplates: CustomTemplate[];
  /** Shapes the shop's administration keeps visible. The wizard offers and enables only these: a shape the admin
   *  hid would be switched on here and then vanish from the sheet as soon as the page is reloaded. */
  allowedShapes?: WizardShape[];
  shapeDefaults?: Partial<Record<WizardShape, WizardShapeDefault>>;
  onIconsChange: (icons: IconType[]) => void;
  /** slotTemplates: the template the customer picked instead of the admin's default, for shapes whose slot is one. */
  onComplete: (name: string, className: string, themeId: string, shapes: WizardShape[], shapeIcons: Partial<Record<WizardShape, IconType>>, shapeThemes: Partial<Record<WizardShape, string>>, shapeWrapModes: Partial<Record<WizardShape, WrapMode>>, selectedTemplate?: { id: string; width: number; height: number }, slotTemplates?: Partial<Record<WizardShape, { id: string; w: number; h: number }>>) => void;
  onSkip: () => void;
}

const WizardIconThumb: React.FC<{ icon: IconType }> = ({ icon }) => {
  if (icon.id === 'no-icon') return <span className="text-gray-400 text-base font-black">—</span>;
  if (icon.component) return <icon.component className="w-full h-full p-0.5" />;
  if (icon.dataType === 'svg' && typeof icon.data === 'string') return <DynamicSvgIcon svgString={icon.data} className="w-full h-full p-0.5" />;
  if (icon.dataType === 'url' && icon.data) return <img src={icon.data as string} alt={icon.name} className="w-full h-full object-contain p-0.5" />;
  return null;
};

const SetupWizard: React.FC<Props> = ({ nameLabel, classNameLabel, defaultNameValue, defaultClassNameValue, kicker, title, intro, icons, categories, allowAi, customTemplates, allowedShapes, shapeDefaults, onIconsChange, onComplete, onSkip }) => {
  const shapes = useMemo(() => {
    const allowed = WIZARD_SHAPES.filter(s => !allowedShapes || allowedShapes.includes(s.id));
    return allowed.length > 0 ? allowed : WIZARD_SHAPES;
  }, [allowedShapes]);
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [themeId] = useState('blue');
  const [previewShape, setPreviewShape] = useState<WizardShape>(shapes[0].id);
  const [shapeIcons, setShapeIcons] = useState<Partial<Record<WizardShape, IconType>>>({});
  const [shapeThemes, setShapeThemes] = useState<Partial<Record<WizardShape, string>>>({});
  const [shapeWrapModes, setShapeWrapModes] = useState<Partial<Record<WizardShape, WrapMode>>>({});
  const [shapeTemplateId, setShapeTemplateId] = useState<Partial<Record<WizardShape, string>>>({});
  const [shapeTemplateDims, setShapeTemplateDims] = useState<Partial<Record<WizardShape, { w: number; h: number }>>>({});
  const [showAiModal, setShowAiModal] = useState(false);

  const noIcon = ICONS.find(i => i.id === 'no-icon') ?? ICONS[0];
  // A shape whose slot the admin turned into a template (only while that template still exists).
  const defaultTemplateOf = (id: WizardShape) => {
    const t = id === 'template' ? undefined : shapeDefaults?.[id]?.template;
    return t && customTemplates.some(c => c.id === t.id) ? t : undefined;
  };
  // Such a slot offers every template right under its own tile; the separate tile that adds one more
  // template sticker is only there when no slot is a template.
  const anyDefaultTemplate = shapes.some(s => defaultTemplateOf(s.id));
  const templateOf = (id: WizardShape) => {
    const base = defaultTemplateOf(id);
    if (!base) return undefined;
    const picked = shapeTemplateId[id];
    if (!picked || !customTemplates.some(c => c.id === picked)) return base;
    return { id: picked, w: shapeTemplateDims[id]?.w ?? base.w, h: shapeTemplateDims[id]?.h ?? base.h };
  };
  const selectedIcon = shapeIcons[previewShape] ?? shapeDefaults?.[previewShape]?.icon ?? noIcon;
  const activeThemeId = shapeThemes[previewShape] ?? themeId;
  const previewDefaultTemplate = templateOf(previewShape);
  const currentPreviewShape = previewShape === 'template'
    ? { id: 'template' as const, label: 'Šablona', w: shapeTemplateDims.template?.w ?? 26, h: shapeTemplateDims.template?.h ?? 26, shapeId: undefined, variantId: '', defaultCount: 0 }
    : previewDefaultTemplate
    ? { ...(shapes.find(s => s.id === previewShape) ?? shapes[0]), label: 'Šablona', w: previewDefaultTemplate.w, h: previewDefaultTemplate.h, shapeId: undefined }
    : (shapes.find(s => s.id === previewShape) ?? shapes[0]);
  const activeWrapMode = shapeWrapModes[previewShape] ?? '1';
  const activeTemplateId = previewShape === 'template' ? shapeTemplateId.template : previewDefaultTemplate?.id;
  const activeWizardTemplate = activeTemplateId ? customTemplates.find(t => t.id === activeTemplateId) : undefined;
  const wizardTemplateHasIcon = !activeWizardTemplate || activeWizardTemplate.hasIcon !== false;

  // A slot keeps the admin's width for whichever template is picked; the extra template sticker is 26 mm wide.
  const selectWizardTemplate = (temp: CustomTemplate, shape: WizardShape = 'template') => {
      const w = defaultTemplateOf(shape)?.w ?? 26;
      setShapeTemplateId(prev => ({ ...prev, [shape]: temp.id }));
      const img = new Image();
      img.onload = () => {
          const ratio = img.naturalWidth && img.naturalHeight ? img.naturalHeight / img.naturalWidth : 1;
          const h = Math.max(3, Math.round(w * ratio * 10) / 10);
          setShapeTemplateDims(prev => ({ ...prev, [shape]: { w, h } }));
      };
      img.onerror = () => setShapeTemplateDims(prev => ({ ...prev, [shape]: { w, h: w } }));
      img.src = temp.data;
  };

  const previewConfig: LabelConfig = useMemo(() => ({
    name: name.trim() || 'Vaše jméno',
    className: className.trim() || '',
    icon: selectedIcon,
    variantSettings: {
      'wizard-preview': {
        iconScale: 0.55,
        fontScale: activeWrapMode === '3' ? 0.6 : activeWrapMode === '2' ? 0.7 : (currentPreviewShape.h < 20 ? 0.7 : 1.0),
        themeId: activeThemeId,
        isBold: true,
        enableShading: true,
        textAlign: 'center',
        wrapText: activeWrapMode !== '1',
        forceTextSplit: activeWrapMode === '3',
        customWidth: currentPreviewShape.w,
        customHeight: currentPreviewShape.h,
        shapeId: currentPreviewShape.shapeId,
        ...(activeTemplateId ? { isFullImage: true, templateId: activeTemplateId } : {}),
      },
    },
  }), [name, className, activeThemeId, currentPreviewShape, selectedIcon, activeWrapMode, activeTemplateId]);

  const fontsVersion = useFontsVersion();
  const svgContent = useMemo(() =>
    generateLabelSvg(previewConfig, 'wizard-preview', 0, 0, currentPreviewShape.w, currentPreviewShape.h, false, [], [], 'wiz', false, customTemplates),
    [previewConfig, currentPreviewShape, customTemplates, fontsVersion]
  );

  const canProceed = step === 1 ? name.trim().length > 0 : true;

  const handleNext = () => {
    if (step < 2) setStep(s => s + 1);
    else {
        const baseShapes = shapes.map(s => s.id) as WizardShape[];
        const templateId = anyDefaultTemplate ? undefined : shapeTemplateId.template;
        const finalShapes = templateId ? [...baseShapes, 'template' as WizardShape] : baseShapes;
        const dims = shapeTemplateDims.template ?? { w: 26, h: 26 };
        const slotTemplates: Partial<Record<WizardShape, { id: string; w: number; h: number }>> = {};
        for (const s of baseShapes) { const t = templateOf(s); if (t) slotTemplates[s] = t; }
        onComplete(name.trim(), className.trim(), themeId, finalShapes, shapeIcons, shapeThemes, shapeWrapModes, templateId ? { id: templateId, width: dims.w, height: dims.h } : undefined, slotTemplates);
    }
  };

  const solidThemes = COLOR_THEMES.filter(t => !t.pattern);

  const displayableIcons = useMemo(() =>
    icons.filter(i => i.id === 'no-icon' || i.component || i.data),
    [icons]
  );

  const stepLabels = ['Jméno', 'Tvary a styl'];

  return (
    <>
    <div className="fixed inset-0 z-[150] bg-black/80 flex items-center justify-center p-2 sm:p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden max-h-[90vh]" style={{ maxHeight: '90dvh', boxShadow: '0 0 0 3px #000, 0 32px 80px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div className="bg-red-600 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 shrink-0">
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div>
              <p className="text-red-200 text-[9px] font-black uppercase tracking-[0.3em] mb-1">{kicker || 'Navrhni si samolepky'}</p>
              <h2 className="text-white font-black text-xl uppercase tracking-tight leading-none">
                {step === 1 && (title || 'Čí samolepky to budou?')}
                {step === 2 && 'Vyber si tvary a styl'}
              </h2>
            </div>
            <button onClick={onSkip} className="text-red-300 hover:text-white text-[9px] font-bold transition-colors mt-1 shrink-0">
              přeskočit
            </button>
          </div>
          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {stepLabels.map((label, idx) => {
              const i = idx + 1;
              const done = i < step;
              const active = i === step;
              return (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black transition-all ${
                      done ? 'bg-white text-red-600' : active ? 'bg-red-800 text-white ring-2 ring-white/40' : 'bg-red-700/50 text-red-300'
                    }`}>
                      {done ? '✓' : i}
                    </div>
                    <span className={`text-[8px] font-black uppercase tracking-widest hidden sm:block transition-colors ${
                      active ? 'text-white' : done ? 'text-red-200' : 'text-red-400'
                    }`}>{label}</span>
                  </div>
                  {idx < 1 && <div className={`flex-1 h-px transition-colors ${done ? 'bg-white/60' : 'bg-red-700/50'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col flex-1 min-h-0">

          {/* Step content */}
          <div className="flex-1 p-4 sm:p-6 flex flex-col gap-4 sm:gap-5 overflow-y-auto">
            {step === 1 && (
              <>
                <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-line">
                  {intro || 'Napiš jméno toho, kdo bude samolepky používat. Objeví se na každé samolepce.'}
                </p>
                <div>
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-2">{nameLabel}</label>
                  <input
                    autoFocus
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={defaultNameValue || 'Petr Novák'}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 font-bold text-base focus:outline-none focus:border-red-500 transition-colors"
                    onKeyDown={e => e.key === 'Enter' && canProceed && handleNext()}
                  />
                </div>
                <div>
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-2">
                    {classNameLabel} <span className="text-gray-300 font-normal normal-case tracking-normal">(volitelné)</span>
                  </label>
                  <input
                    value={className}
                    onChange={e => setClassName(e.target.value)}
                    placeholder={defaultClassNameValue || '3.B'}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 font-bold text-base focus:outline-none focus:border-red-500 transition-colors"
                    onKeyDown={e => e.key === 'Enter' && canProceed && handleNext()}
                  />
                </div>
                <div>
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-2">Zalomit text</label>
                  <div className="flex gap-1">
                    {(['1', '2', '3'] as WrapMode[]).map(mode => (
                      <button key={mode} type="button" onClick={() => setShapeWrapModes(prev => ({ ...prev, ...(Object.fromEntries(ALL_WRAP_TARGETS.map(id => [id, mode])) as Partial<Record<WizardShape, WrapMode>>) }))}
                        className={`flex-1 py-2 text-[9px] font-black rounded-md transition-all uppercase tracking-tighter border-2 ${activeWrapMode === mode ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600'}`}>
                        {mode} {mode === '1' ? 'řádek' : 'řádky'}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-3">
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${shapes.length + (anyDefaultTemplate ? 0 : 1)}, minmax(0, 1fr))` }}>
                  {!anyDefaultTemplate && (
                  <button
                    disabled={customTemplates.length === 0}
                    onClick={() => { setPreviewShape('template'); if (!shapeTemplateId.template) selectWizardTemplate(customTemplates[0]); }}
                    className={`py-2.5 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-xl transition-all ${
                      previewShape === 'template'
                        ? 'bg-red-600 shadow-lg scale-[1.02]'
                        : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-400 hover:bg-gray-100'
                    } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-gray-50`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      <svg className={`w-5 h-5 ${previewShape === 'template' ? 'text-white' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <span className={`text-[9px] font-black uppercase tracking-widest ${previewShape === 'template' ? 'text-white' : 'text-gray-500'}`}>
                      Šablona
                    </span>
                  </button>
                  )}
                  {shapes.map(s => {
                    const isActive = previewShape === s.id;
                    const sTemplate = templateOf(s.id);
                    const sTemplateData = sTemplate ? customTemplates.find(t => t.id === sTemplate.id)?.data : undefined;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setPreviewShape(s.id)}
                        className={`py-2.5 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-xl transition-all ${
                          isActive
                            ? 'bg-red-600 shadow-lg scale-[1.02]'
                            : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        <div className="w-6 h-6 flex items-center justify-center shrink-0">
                          {sTemplateData && <img src={sTemplateData} className="max-w-full max-h-full object-contain" alt="" />}
                          {!sTemplateData && s.id === 'circle' && <div className={`w-5 h-5 rounded-full ${isActive ? 'bg-white/40 border border-white/60' : 'bg-gray-300 border border-gray-400'}`} />}
                          {!sTemplateData && s.id === 'square' && <div className={`w-5 h-5 rounded ${isActive ? 'bg-white/40 border border-white/60' : 'bg-gray-300 border border-gray-400'}`} />}
                          {!sTemplateData && s.id === 'rectangle' && <div className={`w-6 h-3.5 rounded ${isActive ? 'bg-white/40 border border-white/60' : 'bg-gray-300 border border-gray-400'}`} />}
                        </div>
                        <span className={`text-[9px] font-black uppercase tracking-widest ${isActive ? 'text-white' : 'text-gray-500'}`}>
                          {sTemplate ? 'Šablona' : s.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Barva (nebo výběr šablony) + Zalomit text vedle sebe */}
                <div className="grid grid-cols-2 gap-2">
                  {(previewShape === 'template' || previewDefaultTemplate) ? (
                  <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2 border-2 border-gray-100">
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Šablona</p>
                    <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {customTemplates.map(t => (
                        <button
                          key={t.id}
                          title={t.name}
                          onClick={() => selectWizardTemplate(t, previewShape)}
                          className={`aspect-square rounded-md border-2 overflow-hidden transition-all bg-white flex items-center justify-center ${activeTemplateId === t.id ? 'border-red-600 ring-2 ring-red-100 scale-105' : 'border-gray-200 hover:border-gray-400'}`}
                        >
                          <img src={t.data} className="max-w-full max-h-full object-contain" alt={t.name} />
                        </button>
                      ))}
                    </div>
                  </div>
                  ) : (
                  <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2 border-2 border-gray-100">
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Barva</p>
                    <div className="flex flex-wrap gap-1.5">
                      {solidThemes.map(t => {
                        const isChosen = (shapeThemes[previewShape] ?? themeId) === t.id;
                        return (
                          <button
                            key={t.id}
                            title={t.name}
                            onClick={() => setShapeThemes(prev => ({ ...prev, [previewShape]: t.id }))}
                            className={`w-6 h-6 rounded-md transition-all ${isChosen ? 'scale-110 ring-2 ring-black ring-offset-1 shadow-md' : 'hover:scale-105 opacity-70 hover:opacity-100'}`}
                            style={{ backgroundColor: t.primaryHex }}
                          />
                        );
                      })}
                    </div>
                  </div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2 border-2 border-gray-100">
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Zalomit text</p>
                    <div className="flex gap-1">
                      {(['1', '2', '3'] as WrapMode[]).map(mode => (
                        <button key={mode} type="button" onClick={() => setShapeWrapModes(prev => ({ ...prev, [previewShape]: mode }))}
                          className={`flex-1 py-1.5 text-[9px] font-black rounded-md transition-all uppercase tracking-tighter border-2 ${activeWrapMode === mode ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600'}`}>
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Per-shape icon picker */}
                {wizardTemplateHasIcon && (
                <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2 border-2 border-gray-100">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">
                      Ikona pro <span className="text-red-600">{currentPreviewShape.label}</span>
                    </p>
                    {allowAi && (
                      <button onClick={() => setShowAiModal(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-900 text-white text-[8px] font-black uppercase tracking-widest rounded-md">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Vytvoř ikonu pomocí AI
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                    {displayableIcons.map(icon => {
                      const isChosen = selectedIcon.id === icon.id;
                      return (
                        <button
                          key={icon.id}
                          title={icon.name}
                          onClick={() => setShapeIcons(prev => ({ ...prev, [previewShape]: icon }))}
                          className={`w-full aspect-square rounded-lg border-2 flex items-center justify-center transition-all ${
                            isChosen ? 'border-red-600 bg-white shadow-md scale-110' : 'border-gray-200 hover:border-gray-400 bg-white'
                          }`}
                        >
                          <WizardIconThumb icon={icon} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}
              </div>
            )}

          </div>

        </div>

        {/* Live preview strip */}
        <div className="border-t-2 border-gray-100 bg-gray-50 px-4 sm:px-6 py-2 sm:py-4 flex items-center justify-center gap-3 sm:gap-6 shrink-0">
          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest shrink-0">Náhled</p>
          <div className="p-2 sm:p-3 min-w-0">
            <svg
              className="max-h-[14vh] sm:max-h-none"
              viewBox={`0 0 ${currentPreviewShape.w} ${currentPreviewShape.h}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                display: 'block',
                width: `${currentPreviewShape.w * 4}px`,
                height: `${currentPreviewShape.h * 4}px`,
                maxWidth: '100%',
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
                overflow: 'visible',
              }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          </div>
          {step === 2 && (
            <p className="text-[9px] text-gray-400 leading-snug">Klikni na tvar<br/>pro přepnutí</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center shrink-0 bg-gray-50">
          <button
            onClick={() => step > 1 ? setStep(s => s - 1) : onSkip()}
            className="text-[9px] font-bold text-gray-400 hover:text-gray-700 transition-colors uppercase tracking-widest"
          >
            {step > 1 ? '← Zpět' : 'přeskočit'}
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className={`py-2.5 px-8 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
              canProceed
                ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {step === 2 ? 'Vytvořit samolepky →' : 'Dále →'}
          </button>
        </div>
      </div>
    </div>

    {showAiModal && (
      <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
        <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <AiIconGenerator
            categories={categories}
            isAdmin={false}
            onIconGenerated={(base64, prompt, catId) => {
              const newIcon: IconType = { id: `ai-${Date.now()}`, name: prompt, data: base64, dataType: 'url', isCustom: true, categoryId: catId };
              onIconsChange([...icons, newIcon]);
              setShapeIcons(prev => ({ ...prev, [previewShape]: newIcon }));
              setShowAiModal(false);
            }}
            onCancel={() => setShowAiModal(false)}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default SetupWizard;